import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Loader2, Plus, Search, Send, Clock, Play, Pause, Archive, MoreVertical, Users, Mail, Zap, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { MobileFAB } from '../components/MobileFAB';
import { toast } from 'sonner';
import { useSortableTable } from '../hooks/useSortableTable';

const CAMPAIGN_SORT_COLUMNS = [
  { key: 'name', type: 'text' },
  { key: 'audience_name', type: 'text' },
  { key: 'status', type: 'status' },
  { key: 'campaign_type', type: 'text' },
  { key: 'email_count', type: 'number' },
  { key: 'recipient_count', type: 'number' },
];

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
  archived: 'bg-gray-100 text-gray-500',
};

const TYPE_ICON = { automated: Zap, one_time: Send };

export default function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');

  const fetchCampaigns = async () => {
    try {
      const params = {};
      if (tab === 'automated') params.campaign_type = 'automated';
      if (tab === 'one_time') params.campaign_type = 'one_time';
      const res = await campaignsAPI.list(params);
      setCampaigns(res.data);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCampaigns(); }, [tab]);

  const filtered = campaigns.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );
  const { sorted: sortedCampaigns, sortKey, sortDir, onSort } = useSortableTable(filtered, { columns: CAMPAIGN_SORT_COLUMNS });

  const SortIcon = ({ col }) => {
    if (sortKey === col) return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 inline ml-0.5" /> : <ArrowDown className="w-3 h-3 inline ml-0.5" />;
    return <ArrowUpDown className="w-3 h-3 inline ml-0.5 opacity-0 group-hover:opacity-40 transition-opacity" />;
  };

  const handleCreate = async () => {
    try {
      const res = await campaignsAPI.create({ name: 'Untitled Campaign', campaign_type: tab === 'one_time' ? 'one_time' : 'automated' });
      navigate(`/marketing/campaigns/${res.data.id}`);
    } catch { toast.error('Failed to create campaign'); }
  };

  return (
    <Layout>
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">Create and manage email marketing campaigns</p>
        </div>
        <Button onClick={handleCreate} data-testid="create-campaign-btn" className="hidden md:inline-flex">
          <Plus className="w-4 h-4 mr-2" /> New Campaign
        </Button>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex rounded-lg border bg-muted/30 p-0.5 shrink-0 self-start">
          {[
            { key: 'all', label: 'All' },
            { key: 'automated', label: 'Automated' },
            { key: 'one_time', label: 'One-time' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === t.key ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search campaigns..." className="pl-8 h-8 text-xs"
            data-testid="search-campaigns"
          />
        </div>
      </div>

      {/* Campaign List */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <Send className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
          <h3 className="text-sm font-medium text-muted-foreground">No campaigns yet</h3>
          <p className="text-xs text-muted-foreground/60 mt-1">Create your first campaign to start reaching your customers</p>
          <Button size="sm" className="mt-4" onClick={handleCreate} data-testid="empty-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Create Campaign
          </Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {/* Desktop table header */}
          <div className="hidden md:grid grid-cols-[1fr_140px_100px_80px_80px_100px] gap-3 px-4 py-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            <button className="group text-left flex items-center cursor-pointer hover:text-foreground" onClick={() => onSort('name')}>Campaign<SortIcon col="name" /></button>
            <button className="group text-left flex items-center cursor-pointer hover:text-foreground" onClick={() => onSort('audience_name')}>Audience<SortIcon col="audience_name" /></button>
            <button className="group text-left flex items-center cursor-pointer hover:text-foreground" onClick={() => onSort('status')}>Status<SortIcon col="status" /></button>
            <button className="group text-left flex items-center cursor-pointer hover:text-foreground" onClick={() => onSort('campaign_type')}>Type<SortIcon col="campaign_type" /></button>
            <button className="group text-left flex items-center cursor-pointer hover:text-foreground" onClick={() => onSort('email_count')}>Emails<SortIcon col="email_count" /></button>
            <button className="group text-left flex items-center cursor-pointer hover:text-foreground" onClick={() => onSort('recipient_count')}>Recipients<SortIcon col="recipient_count" /></button>
          </div>
          {sortedCampaigns.map(c => {
            const TypeIcon = TYPE_ICON[c.campaign_type] || Send;
            return (
              <Card
                key={c.id}
                onClick={() => navigate(`/marketing/campaigns/${c.id}`)}
                data-testid={`campaign-row-${c.id}`}
                className="cursor-pointer hover:bg-muted/30 transition-colors"
              >
                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-[1fr_140px_100px_80px_80px_100px] gap-3 items-center px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    {c.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{c.description}</p>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{c.audience_name || '—'}</div>
                  <div>
                    <Badge className={`text-[10px] ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}>
                      {c.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <TypeIcon className="w-3 h-3" />
                    <span className="capitalize">{c.campaign_type?.replace('_', '-')}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Mail className="w-3 h-3" /> {c.email_count}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="w-3 h-3" /> {c.recipient_count}
                  </div>
                </div>
                {/* Mobile card */}
                <div className="md:hidden px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate flex-1">{c.name}</p>
                    <Badge className={`text-[10px] shrink-0 ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}>
                      {c.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><TypeIcon className="w-3 h-3" />{c.campaign_type?.replace('_', '-')}</span>
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email_count}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.recipient_count}</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <MobileFAB onClick={handleCreate} data-testid="mobile-fab-campaign" />
    </div>
    </Layout>
  );
}
